/**
 * Container Demo Component
 * 
 * Simple showcase of Container component supported modes:
 * - Flex layout (vertical stacking)
 * - Grid layouts (2, 3, 4, 6 columns)
 * - Padding variations (none, sm, md, lg)
 * - Scroll behaviors (auto, always, none)
 * - Border and label options
 */

import React from 'react';
import { UIBlockRenderer } from '@/components/ai-elements/block-renderers';
import type { RenderPayload, UIActionEvent } from '@/types/ui-blocks';

export const ContainerDemo: React.FC = () => {
  const [selectedMode, setSelectedMode] = React.useState<
    'masonry_free' | 'masonry_2' | 'masonry_3' | 'padding' | 'scroll' | 'bordered'
  >('masonry_free');

  const handleUIAction = (event: UIActionEvent) => {
    console.log('UI Action:', event);
  };

  // ============================================================
  // Sample data for text blocks
  // ============================================================
  const sampleItems = [
    'Item One',
    'Item Two',
    'Item Three',
    'Item Four',
    'Item Five',
    'Item Six',
  ];

  // ============================================================
  // 1. Masonry - Free flow (responsive)
  // ============================================================
  const masonryFreePayload: RenderPayload = {
    version: 'v1',
    title: '📦 Masonry (Free Flow)',
    blocks: [
      {
        type: 'text',
        version: 'v1',
        content: 'Container with **masonry layout** arranges children in a responsive free-flow based on available width.',
      },
      {
        type: 'container',
        version: 'v1',
        layout: 'masonry',
        gap: 16,
        padding: 'md',
        border: true,
        blocks: sampleItems.map((item) => ({
          type: 'text' as const,
          version: 'v1',
          content: `**${item}** - Masonry free-flow item`,
        })),
      },
    ],
  };

  // ============================================================
  // 2. Masonry - 2 Columns (fixed)
  // ============================================================
  const masonry2Payload: RenderPayload = {
    version: 'v1',
    title: '🎨 Masonry - 2 Columns',
    blocks: [
      {
        type: 'text',
        version: 'v1',
        content: 'Container with **masonry layout** and fixed **2 columns**.',
      },
      {
        type: 'container',
        version: 'v1',
        layout: 'masonry',
        columns: 2,
        gap: 16,
        padding: 'md',
        border: true,
        blocks: sampleItems.map((item) => ({
          type: 'text' as const,
          version: 'v1',
          content: `**${item}**\nIn 2-column masonry`,
        })),
      },
    ],
  };

  // ============================================================
  // 3. Masonry - 3 Columns (fixed)
  // ============================================================
  const masonry3Payload: RenderPayload = {
    version: 'v1',
    title: '🎨 Masonry - 3 Columns',
    blocks: [
      {
        type: 'text',
        version: 'v1',
        content: 'Container with **masonry layout** and fixed **3 columns**.',
      },
      {
        type: 'container',
        version: 'v1',
        layout: 'masonry',
        columns: 3,
        gap: 16,
        padding: 'md',
        border: true,
        blocks: sampleItems.map((item) => ({
          type: 'text' as const,
          version: 'v1',
          content: `**${item}**\nIn 3-column masonry`,
        })),
      },
    ],
  };

  // ============================================================
  // 4. (Removed) Masonry - 4 Columns

  // ============================================================
  // 5. Padding Variations
  // ============================================================
  const paddingPayload: RenderPayload = {
    version: 'v1',
    title: '📏 Padding Variations',
    blocks: [
      {
        type: 'text',
        version: 'v1',
        content: 'Different **padding options**: none, sm, md, lg. Compare the internal spacing around borders.',
      },
      {
        type: 'text',
        version: 'v1',
        content: '**None (p-0)**',
      },
      {
        type: 'container',
        version: 'v1',
        layout: 'masonry',
        gap: 12,
        padding: 'none',
        border: true,
        blocks: [
          {
            type: 'text',
            version: 'v1',
            content: 'No padding - content touches border',
          },
        ],
      },
      {
        type: 'text',
        version: 'v1',
        content: '**Small (p-2)**',
      },
      {
        type: 'container',
        version: 'v1',
        layout: 'masonry',
        gap: 12,
        padding: 'sm',
        border: true,
        blocks: [
          {
            type: 'text',
            version: 'v1',
            content: 'Small padding - tight spacing',
          },
        ],
      },
      {
        type: 'text',
        version: 'v1',
        content: '**Medium (p-4)**',
      },
      {
        type: 'container',
        version: 'v1',
        layout: 'masonry',
        gap: 12,
        padding: 'md',
        border: true,
        blocks: [
          {
            type: 'text',
            version: 'v1',
            content: 'Medium padding - comfortable spacing',
          },
        ],
      },
      {
        type: 'text',
        version: 'v1',
        content: '**Large (p-6)**',
      },
      {
        type: 'container',
        version: 'v1',
        layout: 'masonry',
        gap: 12,
        padding: 'lg',
        border: true,
        blocks: [
          {
            type: 'text',
            version: 'v1',
            content: 'Large padding - spacious layout',
          },
        ],
      },
    ],
  };

  // ============================================================
  // 6. Scroll Behaviors
  // ============================================================
  const scrollPayload: RenderPayload = {
    version: 'v1',
    title: '📜 Scroll Behavior Variations',
    blocks: [
      {
        type: 'text',
        version: 'v1',
        content: 'Different **scroll options**: auto (default), always, none. Try resizing or viewing on small screens.',
      },
      {
        type: 'text',
        version: 'v1',
        content: '**Auto (scroll when needed)**',
      },
      {
        type: 'container',
        version: 'v1',
        layout: 'masonry',
        gap: 12,
        padding: 'md',
        border: true,
        height: 200,
        scroll: 'auto',
        blocks: [
          ...sampleItems.map((item) => ({
            type: 'text' as const,
            version: 'v1',
            content: `**${item}** - Scrolls only if content overflows`,
          })),
        ],
      },
      {
        type: 'text',
        version: 'v1',
        content: '**Always (scrollbar always visible)**',
      },
      {
        type: 'container',
        version: 'v1',
        layout: 'masonry',
        gap: 12,
        padding: 'md',
        border: true,
        height: 200,
        scroll: 'always',
        blocks: [
          ...sampleItems.map((item) => ({
            type: 'text' as const,
            version: 'v1',
            content: `**${item}** - Scrollbar always visible`,
          })),
        ],
      },
      {
        type: 'text',
        version: 'v1',
        content: '**None (no scrolling, content clipped)**',
      },
      {
        type: 'container',
        version: 'v1',
        layout: 'masonry',
        gap: 12,
        padding: 'md',
        border: true,
        height: 150,
        scroll: 'none',
        blocks: [
          ...sampleItems.map((item) => ({
            type: 'text' as const,
            version: 'v1',
            content: `**${item}** - No scrolling, content clipped`,
          })),
        ],
      },
    ],
  };

  // ============================================================
  // 7. Bordered & Labeled Containers
  // ============================================================
  const borderedPayload: RenderPayload = {
    version: 'v1',
    title: '🏷️ Bordered & Labeled Containers',
    blocks: [
      {
        type: 'text',
        version: 'v1',
        content: 'Containers can have **labels** (titles) and **borders** for visual grouping.',
      },
      {
        type: 'container',
        version: 'v1',
        label: 'Section with Label & Border',
        border: true,
        layout: 'masonry',
        gap: 12,
        padding: 'md',
        blocks: [
          {
            type: 'text',
            version: 'v1',
            content: 'This container has both a label and a border for visual emphasis.',
          },
          {
            type: 'text',
            version: 'v1',
            content: 'The label appears above as a styled header.',
          },
        ],
      },
      {
        type: 'container',
        version: 'v1',
        label: 'Nested Labeled Sections',
        border: true,
        layout: 'masonry',
        columns: 2,
        gap: 16,
        padding: 'md',
        blocks: [
          {
            type: 'container',
            version: 'v1',
            label: 'Subsection A',
            border: true,
            layout: 'masonry',
            gap: 8,
            padding: 'md',
            blocks: [
              {
                type: 'text',
                version: 'v1',
                content: 'Nested container with its own label and border.',
              },
            ],
          },
          {
            type: 'container',
            version: 'v1',
            label: 'Subsection B',
            border: true,
            layout: 'masonry',
            gap: 8,
            padding: 'md',
            blocks: [
              {
                type: 'text',
                version: 'v1',
                content: 'Another nested container in a 2-column grid.',
              },
            ],
          },
        ],
      },
    ],
  };

  // ============================================================
  // Mode Selection
  // ============================================================
  const payloads: Record<typeof selectedMode, RenderPayload> = {
    masonry_free: masonryFreePayload,
    masonry_2: masonry2Payload,
    masonry_3: masonry3Payload,
    padding: paddingPayload,
    scroll: scrollPayload,
    bordered: borderedPayload,
  };

  const modeLabels: Record<typeof selectedMode, string> = {
    masonry_free: '📦 Masonry (Free Flow)',
    masonry_2: '🎨 Masonry 2 Columns',
    masonry_3: '🎨 Masonry 3 Columns',
    padding: '📏 Padding Variations',
    scroll: '📜 Scroll Behaviors',
    bordered: '🏷️ Bordered & Labeled',
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-6">
      {/* Mode Selector */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4">Container Component Variations</h1>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(modeLabels) as Array<typeof selectedMode>).map((mode) => (
            <button
              key={mode}
              onClick={() => setSelectedMode(mode)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedMode === mode
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              {modeLabels[mode]}
            </button>
          ))}
        </div>
      </div>

      {/* Payload Renderer */}
      <div className="border rounded-lg p-6 bg-white">
        <UIBlockRenderer
          payload={payloads[selectedMode]}
          onUIAction={handleUIAction}
          className="w-full"
        />
      </div>

      {/* Contract Reference */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-sm text-gray-600">
          📚 See <code className="bg-gray-200 px-1 rounded">apps/client/platform/guides/JSON_SCHEMA_FOR_UI_STREAMING.md</code> for full Container contract and examples.
        </p>
      </div>
    </div>
  );
};